/**
 * Minimax AI with alpha-beta pruning for Faanuu.
 * Multiple difficulty levels with distinct playing styles.
 */
import { getLegalMoves, applyMove, checkWinner, countPieces, SIZE } from "./engine";

const PIECE_VALUE = 100;
const KING_VALUE = 280;
const ADVANCE_BONUS = 2;
const CENTER_BONUS = 1;

/** @typedef {'easy' | 'normal' | 'hard' | 'arii'} AILevelId */

/**
 * Difficulty presets — depth, mistake rate, and tie-break noise.
 * @type {Record<AILevelId, { id: AILevelId, label: string, description: string, depth: number, mistakeChance: number, randomness: number, thinkDelay: [number, number] }>}
 */
export const AI_LEVELS = {
  easy: {
    id: "easy",
    label: "Facile",
    description: "Conseils visuels et textuels pour apprendre à jouer.",
    depth: 1,
    mistakeChance: 0.4,
    randomness: 12,
    thinkDelay: [250, 550],
  },
  normal: {
    id: "normal",
    label: "Normal",
    description: "Équilibre tactique, quelques imprécisions possibles.",
    depth: 3,
    mistakeChance: 0.12,
    randomness: 3,
    thinkDelay: [450, 900],
  },
  hard: {
    id: "hard",
    label: "Difficile",
    description: "Anticipe plusieurs coups et privilégie les captures.",
    depth: 4,
    mistakeChance: 0.04,
    randomness: 0.6,
    thinkDelay: [650, 1200],
  },
  arii: {
    id: "arii",
    label: "Ari'i",
    description: "Force maximale — promotion et chaînes de prise optimisées.",
    depth: 6,
    mistakeChance: 0,
    randomness: 0,
    thinkDelay: [900, 1600],
  },
};

export const AI_LEVEL_ORDER = ["easy", "normal", "hard", "arii"];

const DEFAULT_LEVEL = "normal";

export function getAILevel(id) {
  return AI_LEVELS[id] || AI_LEVELS[DEFAULT_LEVEL];
}

export function loadSavedAILevel() {
  try {
    const raw = typeof window !== "undefined" && window.localStorage.getItem("faanuu.aiLevel");
    if (raw && AI_LEVELS[raw]) return raw;
  } catch (_) {
    /* ignore */
  }
  return DEFAULT_LEVEL;
}

export function saveAILevel(id) {
  try {
    if (typeof window !== "undefined") window.localStorage.setItem("faanuu.aiLevel", id);
  } catch (_) {
    /* ignore */
  }
}

export function randomThinkDelay(levelId) {
  const level = getAILevel(levelId);
  const [min, max] = level.thinkDelay;
  return min + Math.floor(Math.random() * (max - min + 1));
}

function evaluate(board, me) {
  let score = 0;
  const mid = (SIZE - 1) / 2;
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const c = board[y][x];
      if (!c) continue;
      const base = c.king ? KING_VALUE : PIECE_VALUE;
      const advance = c.owner === "A" ? y : SIZE - 1 - y;
      const center = Math.max(0, mid - Math.abs(x - mid));
      const val = base + advance * ADVANCE_BONUS + center * CENTER_BONUS;
      score += c.owner === me ? val : -val;
    }
  }
  return score;
}

function minimax(board, depth, alpha, beta, maximizing, me, toMove) {
  const winner = checkWinner(board, toMove);
  if (winner) {
    return winner === me ? 100000 - (10 - depth) : -100000 + (10 - depth);
  }
  if (depth === 0) return evaluate(board, me);

  const moves = getLegalMoves(board, toMove);
  if (!moves.length) return toMove === me ? -100000 : 100000;

  moves.sort((m1, m2) => m2.captures.length - m1.captures.length);

  const next = toMove === "A" ? "B" : "A";
  if (maximizing) {
    let best = -Infinity;
    for (const m of moves) {
      const v = minimax(applyMove(board, m), depth - 1, alpha, beta, false, me, next);
      if (v > best) best = v;
      if (best > alpha) alpha = best;
      if (beta <= alpha) break;
    }
    return best;
  }
  let best = Infinity;
  for (const m of moves) {
    const v = minimax(applyMove(board, m), depth - 1, alpha, beta, true, me, next);
    if (v < best) best = v;
    if (best < beta) beta = best;
    if (beta <= alpha) break;
  }
  return best;
}

function pickSuboptimalMove(moves) {
  if (moves.length <= 1) return moves[0];
  const nonCaptures = moves.filter((m) => m.captures.length === 0);
  const pool = nonCaptures.length ? nonCaptures : moves;
  return pool[Math.floor(Math.random() * pool.length)];
}

function scoreMoves(board, moves, me, depth, randomness) {
  const opp = me === "A" ? "B" : "A";
  return moves.map((m) => {
    const v = minimax(applyMove(board, m), depth - 1, -Infinity, Infinity, false, me, opp);
    return { move: m, score: v + Math.random() * randomness };
  });
}

/**
 * Choose best move for `me` on `board` at the given difficulty level.
 * @param {import('./engine').Board} board
 * @param {'A'|'B'} me
 * @param {AILevelId | number} levelOrDepth — level id or legacy numeric depth
 */
export function chooseAIMove(board, me, levelOrDepth = DEFAULT_LEVEL) {
  const moves = getLegalMoves(board, me);
  if (!moves.length) return null;

  const level =
    typeof levelOrDepth === "number"
      ? getAILevel(
          AI_LEVEL_ORDER[Math.min(Math.max(levelOrDepth - 2, 0), AI_LEVEL_ORDER.length - 1)]
        )
      : getAILevel(levelOrDepth);

  if (level.mistakeChance > 0 && Math.random() < level.mistakeChance) {
    return pickSuboptimalMove(moves);
  }

  if (level.depth <= 0) {
    return pickSuboptimalMove(moves);
  }

  const scored = scoreMoves(board, moves, me, level.depth, level.randomness);
  scored.sort((a, b) => b.score - a.score);
  return scored[0].move;
}

// Suppress unused-import warning for countPieces (kept for potential future heuristics)
export { countPieces };
